import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  message,
  Upload,
  Table,
  Button,
  Alert,
  Spin,
  Tag,
  Popconfirm,
} from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import {
  getDesignDocumentVersionDetail,
  updateDesignDocumentVersion,
} from '@/api/requirements';
import { getUploadUrl } from '@/api/storage';
import type {
  DesignDocumentVersionDetail,
  DesignDocumentAttachment,
  DesignDocumentVersionUpdateRequest,
} from '@/types';
import {
  calculateFileMD5,
  validateUploadResponse,
  verifyUploadedFile,
  cleanupFailedUpload,
} from '@/utils/uploadUtils';
import { uploadLog, uploadError } from '@/utils/uploadLogger';

const { TextArea } = Input;

interface DesignDocumentVersionEditModalProps {
  visible: boolean;
  projectId: number;
  documentId: number;
  versionId: number | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const DesignDocumentVersionEditModal: React.FC<DesignDocumentVersionEditModalProps> = ({
  visible,
  projectId,
  documentId,
  versionId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [versionDetail, setVersionDetail] = useState<DesignDocumentVersionDetail | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  // 已存在附件中，被用户标记删除的 id 集合
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>([]);

  useEffect(() => {
    if (visible && projectId && documentId && versionId) {
      loadVersionDetail();
    }
    if (!visible) {
      // 关闭时重置内部状态，避免下次打开残留
      setVersionDetail(null);
      setFileList([]);
      setDeletedAttachmentIds([]);
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, projectId, documentId, versionId]);

  const loadVersionDetail = async () => {
    if (!projectId || !documentId || !versionId) return;
    try {
      setDetailLoading(true);
      const data = await getDesignDocumentVersionDetail(projectId, documentId, versionId);
      setVersionDetail(data);
      setDeletedAttachmentIds([]);
      setFileList([]);
      form.setFieldsValue({
        file_description: data.file_description,
        features: data.features,
        requirement_attribute: data.requirement_attribute,
        documents: (data.documents || []).join('\n'),
        remarks: data.remarks || '',
        user_version: data.user_version || '',
        version_remarks: data.version_remarks || '',
      });
    } catch (error) {
      console.error('Failed to load version detail:', error);
      message.error('加载版本详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const parseDocumentUrls = (text: string): string[] => {
    return (text || '')
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url.length > 0);
  };

  const handleUploadChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const handleUploadBeforeUpload = async () => {
    // 阻止自动上传，改为提交时手动上传
    return false;
  };

  const handleMarkDeleteAttachment = (attachmentId: number) => {
    setDeletedAttachmentIds((prev) =>
      prev.includes(attachmentId) ? prev : [...prev, attachmentId]
    );
  };

  const handleUndoDeleteAttachment = (attachmentId: number) => {
    setDeletedAttachmentIds((prev) => prev.filter((id) => id !== attachmentId));
  };

  const uploadAttachments = async (): Promise<any[]> => {
    const uploaded: any[] = [];
    const failedObjectKeys: string[] = [];

    for (const uploadFile of fileList) {
      if (!uploadFile.originFileObj) continue;
      const fileToUpload = uploadFile.originFileObj as File;

      try {
        setUploadingFiles((prev) => new Set([...prev, uploadFile.uid]));

        const md5 = await calculateFileMD5(fileToUpload);
        uploadLog(`[Upload:MD5] ${uploadFile.name}: ${md5}`);

        const signedUrlResponse = await getUploadUrl(
          `design-documents/${Date.now()}_${uploadFile.name}`,
          fileToUpload.type || 'application/octet-stream'
        );
        const objectKey = signedUrlResponse.object_key;
        uploadLog(`[Upload:SignedUrl] ${uploadFile.name}: object_key=${objectKey}`);

        const response = await fetch(signedUrlResponse.url, {
          method: 'PUT',
          body: fileToUpload,
          headers: {
            'Content-Type': fileToUpload.type || 'application/octet-stream',
          },
        });

        await validateUploadResponse(response, md5, objectKey, uploadFile.name);

        try {
          await verifyUploadedFile(objectKey, fileToUpload.size, uploadFile.name);
        } catch (verifyError) {
          uploadError(`[Upload:VerificationFailed] ${uploadFile.name}:`, verifyError);
          failedObjectKeys.push(objectKey);
          throw new Error(
            `File verification failed after upload: ${
              verifyError instanceof Error ? verifyError.message : 'Unknown error'
            }`
          );
        }

        uploaded.push({
          file_name: uploadFile.name,
          file_url: objectKey,
          object_key: objectKey,
          file_size: fileToUpload.size,
        });

        uploadLog(`[Upload:Success] ${uploadFile.name} uploaded and verified`);
        message.success(`${uploadFile.name} 上传成功`);
      } catch (error) {
        uploadError(`[Upload:Failed] ${uploadFile.name}:`, error);
        message.error(
          `${uploadFile.name} 上传失败: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        );
      } finally {
        setUploadingFiles((prev) => {
          const newSet = new Set(prev);
          newSet.delete(uploadFile.uid);
          return newSet;
        });
      }
    }

    for (const objectKey of failedObjectKeys) {
      try {
        await cleanupFailedUpload(objectKey, 'unknown');
      } catch {
        // 清理失败已记录日志
      }
    }

    return uploaded;
  };

  const handleSubmit = async () => {
    if (!versionId) return;

    try {
      const values = await form.validateFields();
      const documentUrls = parseDocumentUrls(values.documents);

      setLoading(true);

      const newAttachments = await uploadAttachments();

      const payload: DesignDocumentVersionUpdateRequest = {
        file_description: values.file_description,
        features: values.features,
        requirement_attribute: values.requirement_attribute,
        documents: documentUrls,
        remarks: values.remarks || '',
        user_version: values.user_version || '',
        version_remarks: values.version_remarks || '',
        deleted_attachment_ids: deletedAttachmentIds,
        new_attachments: newAttachments,
      };

      await updateDesignDocumentVersion(projectId, documentId, versionId, payload);
      message.success('版本更新成功');

      setFileList([]);
      onSuccess();
      onCancel();
    } catch (error: any) {
      uploadError('Version update failed:', error);
      let errorMsg = '更新失败';

      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error?.message) {
        errorMsg = error.message;
      }

      uploadError('完整错误信息:', error?.response?.data);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const existingAttachments = versionDetail?.attachments || [];
  const visibleAttachments = existingAttachments.filter(
    (a) => !deletedAttachmentIds.includes(a.id)
  );
  const removedAttachments = existingAttachments.filter((a) =>
    deletedAttachmentIds.includes(a.id)
  );

  return (
    <Modal
      title={
        versionDetail
          ? `编辑版本 - V${versionDetail.version_number}`
          : '编辑版本'
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={760}
      okText="保存"
      cancelText="取消"
      confirmLoading={loading}
      okButtonProps={{ disabled: detailLoading || uploadingFiles.size > 0 }}
    >
      {detailLoading ? (
        <Spin />
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            label="文件描述"
            name="file_description"
            rules={[
              { required: true, message: '请输入文件描述' },
              { min: 5, message: '至少5个字符' },
              { max: 1000, message: '最多1000个字符' },
            ]}
          >
            <TextArea
              rows={3}
              placeholder="请输入文件描述"
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item
            label="Features"
            name="features"
            rules={[{ required: true, message: '请选择Features' }]}
          >
            <Select placeholder="请选择">
              <Select.Option value="MCU">MCU</Select.Option>
              <Select.Option value="SOC">SOC</Select.Option>
              <Select.Option value="MCU&SOC">MCU&SOC</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="需求属性"
            name="requirement_attribute"
            rules={[{ required: true, message: '请选择需求属性' }]}
          >
            <Select placeholder="请选择">
              <Select.Option value="必选">必选</Select.Option>
              <Select.Option value="可选">可选</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="文档链接"
            name="documents"
            help="每行一个URL链接，最多10个"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const urls = parseDocumentUrls(value);
                  if (urls.length > 10) {
                    return Promise.reject(new Error('最多支持10个文档链接'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={
                '每行一个URL链接\\nhttps://example.com/doc1\\nhttps://example.com/doc2'
              }
            />
          </Form.Item>

          <Form.Item label="备注" name="remarks">
            <TextArea rows={2} placeholder="可选" />
          </Form.Item>

          <Form.Item
            label="版本"
            name="user_version"
            help="可选，用于标识本次发布的版本号（如V1.0.0, V2.1.3等）"
            rules={[{ max: 50, message: '版本号最多50个字符' }]}
          >
            <Input placeholder="请输入版本号（可选）" maxLength={50} />
          </Form.Item>

          <Form.Item label="更新说明" name="version_remarks">
            <TextArea
              rows={3}
              placeholder="可选，描述本次编辑的内容和原因"
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item label="版本附件">
            {visibleAttachments.length > 0 ? (
              <Table<DesignDocumentAttachment>
                dataSource={visibleAttachments}
                rowKey="id"
                size="small"
                pagination={false}
                style={{ marginBottom: 12 }}
                columns={[
                  {
                    title: '文件名',
                    dataIndex: 'file_name',
                    width: '45%',
                  },
                  {
                    title: '大小',
                    dataIndex: 'file_size',
                    width: '20%',
                    render: (size: number) => formatFileSize(size),
                  },
                  {
                    title: '上传人',
                    dataIndex: ['created_by', 'display_name'],
                    width: '20%',
                  },
                  {
                    title: '操作',
                    width: '15%',
                    render: (_, record) => (
                      <Popconfirm
                        title="确定删除这个附件吗？"
                        onConfirm={() => handleMarkDeleteAttachment(record.id)}
                      >
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                        >
                          删除
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            ) : (
              <div style={{ color: '#999', marginBottom: 12 }}>暂无附件</div>
            )}

            {removedAttachments.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`已标记删除 ${removedAttachments.length} 个附件，保存后生效`}
                description={
                  <div>
                    {removedAttachments.map((a) => (
                      <Tag
                        key={a.id}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          handleUndoDeleteAttachment(a.id);
                        }}
                        style={{ marginBottom: 4 }}
                      >
                        {a.file_name}
                      </Tag>
                    ))}
                  </div>
                }
              />
            )}

            <Upload
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={handleUploadBeforeUpload}
              multiple
            >
              <Button icon={<UploadOutlined />}>上传新附件</Button>
            </Upload>
            {fileList.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <p>待上传文件：</p>
                <Table
                  dataSource={fileList}
                  rowKey="uid"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '文件名', dataIndex: 'name', width: '60%' },
                    {
                      title: '大小',
                      width: '20%',
                      render: (_, record) => formatFileSize(record.size || 0),
                    },
                    {
                      title: '状态',
                      width: '20%',
                      render: (_, record) => {
                        if (uploadingFiles.has(record.uid)) return '上传中...';
                        if (record.status === 'done') return '已上传';
                        return '待上传';
                      },
                    },
                  ]}
                />
              </div>
            )}
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default DesignDocumentVersionEditModal;
